import { consoleRoutes, RATING_STARS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon, Skeleton, cssVars } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import type { AnalyticsFilters } from "./analytics-model";
import { ChartCard } from "./chart-card";
import { useSummary } from "./use-analytics";

const AVERAGE_DIGITS = 1;
const PERCENT = 100;

function Stars({ average }: { average: number }) {
  const filled = Math.round(average);
  return (
    <span className="flex items-center gap-0.5 text-primary-ink" aria-hidden="true">
      {Array.from({ length: RATING_STARS }, (_, index) => <Icon key={index} name="star" className={index < filled ? "size-4 fill-current" : "size-4 text-fg-subtle"} />)}
    </span>
  );
}

/** Customer satisfaction from "Ask for a rating" steps: how many answered, the average and the share of 4-5 star or thumbs-up ratings. */
export function CsatCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { summary: workspace } = useWorkspaceContext();
  const query = useSummary(filters);
  return (
    <ChartCard
      testId="analytics-csat"
      title={
        <>
          <Icon name="thumbs-up" className="size-4 text-primary-ink" />
          {t("engage.analytics.csat.title")}
        </>
      }
      aside={<AppLink href={consoleRoutes.workflows(workspace.slug)} className="text-xs font-semibold text-primary-ink hover:underline">{t("engage.analytics.csat.collect")}</AppLink>}
      query={query}
      isEmpty={({ csat }) => csat.responses === 0 || csat.average === null}
      empty={{ icon: "star", title: t("engage.analytics.csat.emptyTitle"), body: t("engage.analytics.csat.emptyBody") }}
      skeleton={<div className="grid grid-cols-3 gap-3">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-20" />)}</div>}
      bodyClassName="space-y-4"
    >
      {({ csat }) => {
        const average = csat.average ?? 0;
        const positive = csat.positive_rate ?? 0;
        return (
          <>
            <dl className="grid grid-cols-3 gap-3">
              <div className="rounded-md bg-surface-2 p-3">
                <dt className="text-[11px] font-semibold text-fg-subtle">{t("engage.analytics.csat.responses")}</dt>
                <dd className="mt-0.5 text-xl font-extrabold tabular-nums" data-testid="csat-responses">{fmt.number(csat.responses)}</dd>
              </div>
              <div className="rounded-md bg-surface-2 p-3">
                <dt className="text-[11px] font-semibold text-fg-subtle">{t("engage.analytics.csat.average")}</dt>
                <dd className="mt-0.5 text-xl font-extrabold tabular-nums" data-testid="csat-average">
                  {average.toFixed(AVERAGE_DIGITS)}
                  <span className="text-sm font-bold text-fg-subtle">/{RATING_STARS}</span>
                </dd>
                <Stars average={average} />
              </div>
              <div className="rounded-md bg-surface-2 p-3">
                <dt className="text-[11px] font-semibold text-fg-subtle">{t("engage.analytics.csat.positive")}</dt>
                <dd className="mt-0.5 text-xl font-extrabold tabular-nums" data-testid="csat-positive">{fmt.percent(positive)}</dd>
              </div>
            </dl>
            <div role="meter" aria-label={t("engage.analytics.csat.positive")} aria-valuemin={0} aria-valuemax={PERCENT} aria-valuenow={Math.round(positive * PERCENT)} className="h-1.5 rounded-pill bg-sunken">
              <div className="h-1.5 w-[var(--geo-width)] rounded-pill bg-mint-500" style={cssVars({ "--geo-width": `${(positive * PERCENT).toFixed(1)}%` })} />
            </div>
            <p className="text-xs text-fg-subtle">{t("engage.analytics.csat.note")}</p>
          </>
        );
      }}
    </ChartCard>
  );
}
